// Sugalarm — Cycle 400: threshold handle geometry.
//
// Pure, dependency-free, no React and no DOM. That is the point: the
// collision behavior below is the part most likely to regress and the
// part a browser is worst at proving, so it lives here where a unit
// test can drive it directly. `ThresholdSlider` supplies measured
// pixel values and renders whatever this returns.
//
// WHY THIS MODULE EXISTS AT ALL
// -----------------------------
// Cycle 74 demoted the track to a read-only visual and cycle 129 made
// the 20×20px markers draggable again. Users reported the handles were
// "too small and not responding". Reading the cycle-129 implementation:
// `touch-action: none`, `setPointerCapture` and `onPointerCancel` were
// ALL already present and correct. The gesture was never being stolen
// from a grabbed handle — the handle was being MISSED. A 20px target on
// a 12px track is well under the 44px floor, and a press that misses it
// lands on a track with no pointer handler, which the pane then reads
// as a scroll. Cycle 400 replaces the markers with 66x44px labelled
// pills that sit exactly on their values and are the only draggable thing.
//
// A first draft carried a `resolveRowPositions` collision resolver that
// nudged crowded pills apart and drew sloped leader lines back to the true
// value. It was removed on the bench (operator direction): a nudged pill
// is no longer under the finger aimed at it, and the diagonal leaders read
// as a bug. Crowded pills now simply overlap, with the dragged one raised.

export type ThresholdKey =
  | "urgent_low"
  | "target_low"
  | "target_high"
  | "urgent_high";

/** Which side of the track each pill renders on.
 *
 *  The stagger is fixed rather than computed, for two reasons: a pill
 *  that changes rows as the user drags is disorienting, and the fixed
 *  assignment encodes the safety hierarchy for free — the urgent pair
 *  sits below the track, the target pair above it.
 *
 *  `target_low` and `target_high` share a row, as do the urgent pair; when
 *  two neighbours crowd, the pills overlap and the dragged one raises. */
export const HANDLE_ROW: Record<ThresholdKey, "upper" | "lower"> = {
  urgent_low: "lower",
  target_low: "upper",
  target_high: "upper",
  urgent_high: "lower",
};

/** Drag is the coarse tool. One mg/dL is ~2.4px on the kiosk track and
 *  ~0.9px on a 393px phone, so an unstepped drag turns finger tremor
 *  into threshold drift. */
export const DRAG_STEP_MGDL = 5;

/** The steppers stay the precision tool, so a user who wants exactly 72
 *  can still have it. Keyboard arrows use this too. */
export const STEPPER_STEP_MGDL = 1;

/**
 * Snap-and-clamp, in mg/dL space only.
 *
 * Every caller converts to mg/dL before calling and converts back for
 * display afterwards, so a user on mmol/L drags the same underlying
 * grid as a user on mg/dL and the wire format never changes.
 *
 * Snapping happens before clamping and the clamp wins, so the result is
 * never outside [min, max] even when the bounds are not themselves
 * multiples of `step`.
 */
export function snapToStep(
  value: number,
  step: number,
  min: number,
  max: number,
): number {
  if (!Number.isFinite(value)) return min;
  const s = step > 0 ? step : 1;
  const snapped = Math.round(value / s) * s;
  return Math.min(max, Math.max(min, snapped));
}
